import TextAreaInput from 'ext-ui/components/TextAreaInput/TextAreaInput'
import TextInput from 'ext-ui/components/TextInput/TextInput'
import React from 'react'
import CountryPhoneDropdown from 'src/components/CountryPhoneDropdown/CountryPhoneDropdown'
import FieldLabel from 'src/components/FieldLabel/FieldLabel'
import PhoneTextInputField from 'src/components/PhoneTextInputField/PhoneTextInputField'
import { useTranslate } from 'src/localization/Trns'

import type { VendorTextField } from './useVendorDetailsForm'
import { fields, phoneField, phoneInput, phoneRow } from './VendorFlyout.style'
import { VendorField, VendorFormValues } from './vendorForm.validation'

export interface VendorDetailsTabProps {
  /** id of this panel, referenced by the tab's aria-controls. */
  id: string
  /** id of the tab that labels this panel. */
  labelledBy: string
  values: VendorFormValues
  fieldError: (field: VendorField) => string | undefined
  onTextFieldChange: (field: VendorTextField, value: string) => void
  onPhoneChange: (value: string) => void
  onPhoneCountryChange: (iso: string) => void
  /** Archived vendors render read-only: same fields + values, no editing. */
  readOnly?: boolean
  /** The stored E.164 phone, shown as a disabled field in read-only mode. */
  phoneDisplayValue?: string
}

/**
 * The vendor's six editable Step-1 fields (EX-40771), reusing the add form's
 * exact TextInput / TextAreaInput / CountryPhoneDropdown + PhoneTextInputField
 * composition so add and edit read identically. Purely presentational: all form
 * state, validation, and the dirty check live in useVendorDetailsForm; the tab
 * label is the only heading (there is no section title inside the tab).
 */
const VendorDetailsTab: React.FC<VendorDetailsTabProps> = ({
  id,
  labelledBy,
  values,
  fieldError,
  onTextFieldChange,
  onPhoneChange,
  onPhoneCountryChange,
  readOnly = false,
  phoneDisplayValue,
}) => {
  const translate = useTranslate()

  return (
    <div css={fields} id={id} role={'tabpanel'} aria-labelledby={labelledBy}>
      <TextInput
        label={translate('Vendor Name')}
        requirement={'required'}
        placeholder={translate('Enter vendor name')}
        value={values.name}
        disabled={readOnly}
        onChange={(event) => onTextFieldChange('name', event.target.value)}
        errorText={fieldError('name')}
        testId={'vendor-details-name'}
      />
      <TextInput
        label={translate('Vendor Email')}
        requirement={'required'}
        type={'email'}
        inputMode={'email'}
        placeholder={translate('Enter vendor email')}
        value={values.email}
        disabled={readOnly}
        onChange={(event) => onTextFieldChange('email', event.target.value)}
        errorText={fieldError('email')}
        tooltipProps={{
          children: translate(
            'Payment remittance details are sent to this address every time you pay this vendor.',
          ),
        }}
        testId={'vendor-details-email'}
      />
      <TextInput
        label={translate('Vendor ID')}
        placeholder={translate('Enter vendor ID')}
        value={values.externalId}
        disabled={readOnly}
        onChange={(event) =>
          onTextFieldChange('externalId', event.target.value)
        }
        errorText={fieldError('externalId')}
        tooltipProps={{
          children: translate(
            "Your organization's own identifier for this vendor, like an ID from your accounting system.",
          ),
        }}
        testId={'vendor-details-external-id'}
      />
      <TextInput
        label={translate('Contact Name')}
        placeholder={translate('Enter contact name')}
        value={values.contactName}
        disabled={readOnly}
        onChange={(event) =>
          onTextFieldChange('contactName', event.target.value)
        }
        errorText={fieldError('contactName')}
        tooltipProps={{
          children: translate(
            'Your accounts payable contact at this vendor — who to reach for billing questions.',
          ),
        }}
        testId={'vendor-details-contact-name'}
      />
      {readOnly ? (
        <TextInput
          label={translate('Phone Number')}
          value={phoneDisplayValue ?? ''}
          disabled
          testId={'vendor-details-phone'}
        />
      ) : (
        <div css={phoneField}>
          <FieldLabel htmlFor={'vendorEditPhoneNumber'}>
            {translate('Phone Number')}
          </FieldLabel>
          <div css={phoneRow}>
            <CountryPhoneDropdown
              id={'vendorEdit'}
              selectedValue={values.phoneIsoCountry}
              chooseOption={onPhoneCountryChange}
            />
            <PhoneTextInputField
              id={'vendorEdit'}
              css={phoneInput}
              countryIso={values.phoneIsoCountry}
              value={values.phone}
              onChange={onPhoneChange}
              errors={
                fieldError('phone') ? [fieldError('phone') as string] : undefined
              }
            />
          </div>
        </div>
      )}
      <TextAreaInput
        aria-label={translate('Vendor Description')}
        placeholder={translate('Enter vendor description')}
        value={values.notes}
        disabled={readOnly}
        onChange={(event) => onTextFieldChange('notes', event.target.value)}
        errorText={fieldError('notes')}
        rows={3}
        testId={'vendor-details-notes'}
      />
    </div>
  )
}

export default VendorDetailsTab
